import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { Dialogo } from './Dialogo';

/** `perigo: false` para confirmar o que não tira nada do quadro (ex.: tornar um post-it público): botão na cor primária. */
export type PedidoConfirmacao = { titulo: string; mensagem: ReactNode; rotulo: string; perigo?: boolean };
type Aberto = PedidoConfirmacao & { comMotivo: boolean; responder: (resposta: string | null) => void };
type Api = { confirmar(pedido: PedidoConfirmacao): Promise<boolean>; pedirMotivo(pedido: PedidoConfirmacao): Promise<string | null> };

const Contexto = createContext<Api | null>(null);

function usarApi() {
  const api = useContext(Contexto);
  if (!api) throw new Error('ProvedorConfirmacao ausente');
  return api;
}

/** Toda exclusão pergunta antes (caixa modal em vermelho) e só segue com a confirmação da pessoa. */
export const useConfirmar = () => usarApi().confirmar;

/** Exclusão que exige motivo (exclusão lógica): devolve o motivo digitado ou null se a pessoa cancelou. */
export const usePedirMotivo = () => usarApi().pedirMotivo;

export function ProvedorConfirmacao({ children }: { children: ReactNode }) {
  const [aberto, definirAberto] = useState<Aberto | null>(null);
  const [motivo, definirMotivo] = useState('');
  const abrir = useCallback(
    (pedido: PedidoConfirmacao, comMotivo: boolean) =>
      new Promise<string | null>((responder) => {
        definirMotivo('');
        definirAberto({ ...pedido, comMotivo, responder });
      }),
    [],
  );
  const api = useMemo<Api>(() => ({ confirmar: async (p) => (await abrir(p, false)) !== null, pedirMotivo: (p) => abrir(p, true) }), [abrir]);
  const fechar = (resposta: string | null) => {
    aberto?.responder(resposta);
    definirAberto(null);
  };
  return (
    <Contexto.Provider value={api}>
      {children}
      {aberto && (
        <Dialogo
          titulo={aberto.titulo}
          descricao={aberto.mensagem}
          rotuloConfirmar={aberto.rotulo}
          perigo={aberto.perigo ?? true}
          podeConfirmar={!aberto.comMotivo || motivo.trim().length >= 3}
          aoConfirmar={() => fechar(aberto.comMotivo ? motivo.trim() : '')}
          aoCancelar={() => fechar(null)}
        >
          {aberto.comMotivo && (
            <label className="campo">
              <span>Motivo</span>
              <input value={motivo} maxLength={500} autoFocus onChange={(e) => definirMotivo(e.target.value)} />
            </label>
          )}
        </Dialogo>
      )}
    </Contexto.Provider>
  );
}
